import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  styled
} from '@mui/material';
import Menu from '@mui/material/Menu';
import {
  Search as SearchIcon,
  Settings as SettingsIcon,
  Category as CategoryIcon,
  InfoOutlined,
  Close as CloseIcon,
  Rule as RuleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useScrollableFilters } from '../hooks/useScrollableFilters';
import ScrollableFilters from './ScrollableFilters';
import InfoBanner from '../../common/InfoBanner';
import CategoryChip from '../../common/CategoryChip';
import ListFooter from '../../common/ListFooter';
import ResponsiveListTable from '../../common/ResponsiveListTable';
import { useData } from '../../../contexts/DataContext';
import SortIcon from '@mui/icons-material/Sort';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';

// Shared dropdown behaviour for every category picker on this card: cap the
// menu height so long category lists scroll internally instead of covering
// the screen (critical on mobile), and keep rows compact but touch-friendly.
const CATEGORY_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: { xs: '50vh', sm: 320 },
      mt: 0.5,
      '& .MuiMenuItem-root': {
        fontSize: '0.875rem',
        minHeight: { xs: 44, sm: 36 }
      }
    }
  }
};

// Colored dot shown next to a category name in pickers and menus
const CategoryDot = ({ color }) => (
  <Box
    component="span"
    sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color || '#999', flexShrink: 0 }}
  />
);

// Inline field layout helpers (match Transactions List)
const InlineRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  flexWrap: 'wrap'
}));

const InlineLabel = styled(Box)(({ theme }) => ({
  minWidth: 110,
  color: theme.palette.text.secondary,
  fontWeight: 600,
  fontSize: theme.typography.pxToRem(14)
}));

const InlineInputBox = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 220
}));

const TransactionReviewCard = ({
  transactions,
  categories,
  filters,
  onFilterChange,
  onCreateRuleFromTransaction,
  onBulkCategorize,
  getCategoryName,
  formatRelativeDate,
  sorting,
  onSortChange,
  aiSuggestions = [],
  onAcceptAiSuggestion,
  onRejectAiSuggestion,
  onAiCategorizeSelected,
  aiCategorizing
}) => {
  const { accounts } = useData();

  const aiSuggestionsById = new Map(aiSuggestions.map(s => [s.id, s]));

  // Show the account's display name instead of its raw id
  const getAccountName = (accountId) => {
    if (!accountId) return '-';
    const acc = (accounts || []).find(a => a.id === accountId || a.name === accountId);
    return acc ? acc.name : accountId;
  };

  const {
    reviewFilterStartDate,
    reviewFilterEndDate,
    reviewFilterDescription,
    reviewFilterAccount,
    reviewFilterCategory,
    reviewPage,
    reviewRowsPerPage,
    filterTransactions,
    getPaginatedTransactions,
    getActiveFilterCount,
    clearAllFilters,
    handlePageChange,
    handleRowsPerPageChange,
    openAllFiltersDialog,
    closeAllFiltersDialog,
    clearAllFiltersAndCloseDialog,
    updateReviewFilters,
    allFiltersDialogOpen
  } = filters;

  const {
    txnSort,
    handleTxnSortClick,
    handleTxnSortClose,
    handleTxnSortChange,
    txnSortAnchorEl
  } = sorting;

  // The quick category menu anchors to a screen position (not an element) so
  // it works from both the desktop row icon and the mobile kebab menu, whose
  // menu item unmounts the moment it is clicked.
  const [categoryMenuPos, setCategoryMenuPos] = useState(null);
  const [categoryMenuTxnId, setCategoryMenuTxnId] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTxn, setViewTxn] = useState(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkCategory, setBulkCategory] = useState('');

  const reviewFiltersScroll = useScrollableFilters();

  const formatLocalDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString || '';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(amount || 0));
  };

  // Get filtered and paginated transactions
  const getSortedTransactions = (txns) => {
    let arr = [...txns];
    if (txnSort.type === 'date') {
      arr.sort((a, b) => {
        const aDate = new Date(a.date || 0);
        const bDate = new Date(b.date || 0);
        return txnSort.direction === 'desc' ? bDate - aDate : aDate - bDate;
      });
    } else if (txnSort.type === 'amount') {
      arr.sort((a, b) =>
        txnSort.direction === 'desc' ? b.amount - a.amount : a.amount - b.amount
      );
    } else if (txnSort.type === 'description') {
      arr.sort((a, b) => {
        const cmp = (a.description || '').localeCompare(b.description || '');
        return txnSort.direction === 'asc' ? cmp : -cmp;
      });
    }
    return arr;
  };

  const filtered = filterTransactions(getSortedTransactions(transactions));
  const paginated = getPaginatedTransactions(filtered);

  // Selection helpers
  const filteredIds = filtered.map(t => t._id || t.id);

  const handleToggleSelect = (txnId) => {
    setSelectedIds(prev =>
      prev.includes(txnId) ? prev.filter(id => id !== txnId) : [...prev, txnId]
    );
  };

  const handleBulkCategorize = async () => {
    if (selectedIds.length > 0 && bulkCategory) {
      await onBulkCategorize(selectedIds, bulkCategory);
      setSelectedIds([]);
      setBulkCategory('');
    }
  };

  const openCategoryMenu = (event, txnId) => {
    setCategoryMenuPos({ top: event.clientY, left: event.clientX });
    setCategoryMenuTxnId(txnId);
  };

  const closeCategoryMenu = () => {
    setCategoryMenuPos(null);
    setCategoryMenuTxnId(null);
  };

  const handleSelectCategory = async (categoryId) => {
    if (categoryMenuTxnId && categoryId) {
      await onBulkCategorize([categoryMenuTxnId], categoryId);
    }
    closeCategoryMenu();
  };

  const renderFilters = () => (
    <Box sx={{ 
      display: 'flex', 
      overflow: 'hidden', 
      width: '100%', 
      maxWidth: '100%', 
      px: { xs: 1, sm: 2 }, 
      pt: 2, 
      position: 'relative', 
      flexDirection: { xs: 'row', sm: 'row' }, 
      gap: { xs: 1, sm: 0 }, 
      alignItems: 'center' 
    }}>
      <ScrollableFilters
        scrollRef={reviewFiltersScroll.scrollRef}
        showLeftScroll={reviewFiltersScroll.showLeftScroll}
        showRightScroll={reviewFiltersScroll.showRightScroll}
        onScrollLeft={reviewFiltersScroll.scrollLeft}
        onScrollRight={reviewFiltersScroll.scrollRight}
      >
        <Button
          variant="outlined"
          onClick={openAllFiltersDialog}
          startIcon={<SettingsIcon fontSize="small" />}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderColor: getActiveFilterCount() > 0 ? 'primary.main' : 'grey.300',
            backgroundColor: getActiveFilterCount() > 0 ? 'primary.lightest' : 'grey.50',
            color: getActiveFilterCount() > 0 ? 'primary.dark' : 'grey.700',
            borderRadius: 2,
            fontWeight: 500,
            fontSize: '0.875rem',
            height: 37,
            minWidth: 120,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: getActiveFilterCount() > 0 ? 'primary.lightest' : 'grey.100',
              borderColor: getActiveFilterCount() > 0 ? 'primary.dark' : 'grey.400',
            }
          }}
        >
          All filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
        </Button>
        
        <TextField
          label="Start Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={reviewFilterStartDate}
          onChange={e => updateReviewFilters.setStartDate(e.target.value)}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#fff',
              '&:hover': { backgroundColor: '#fff' },
              '&.Mui-focused': { backgroundColor: '#fff' }
            }
          }}
          inputProps={{ style: { fontSize: '0.875rem' } }}
        />
        
        <TextField
          label="End Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={reviewFilterEndDate}
          onChange={e => updateReviewFilters.setEndDate(e.target.value)}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#fff',
              '&:hover': { backgroundColor: '#fff' },
              '&.Mui-focused': { backgroundColor: '#fff' }
            }
          }}
          inputProps={{ style: { fontSize: '0.875rem' } }}
        />
        
        <FormControl 
          size="small" 
          sx={{ 
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#fff',
              '&:hover': { backgroundColor: '#fff' },
              '&.Mui-focused': { backgroundColor: '#fff' }
            }
          }}
        >
          <InputLabel sx={{ fontSize: '0.875rem' }} shrink>Category</InputLabel>
          <Select
            value={reviewFilterCategory}
            label="Category"
            onChange={e => updateReviewFilters.setCategory(e.target.value)}
            displayEmpty
            MenuProps={CATEGORY_MENU_PROPS}
            renderValue={(selected) => {
              // Show "All Categories" when value is empty or undefined
              if (!selected || selected === '') return 'All Categories';
              if (selected === 'uncategorized') return 'Uncategorized';
              const cat = categories.find(c => c.id === selected);
              return cat ? cat.name : 'All Categories';
            }}
            sx={{ fontSize: '0.875rem' }}
          >
            <MenuItem value="">All Categories</MenuItem>
            <MenuItem value="uncategorized">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryDot color="#999" />
                Uncategorized
              </Box>
            </MenuItem>
            {categories.map(cat => (
              <MenuItem key={cat.id} value={cat.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CategoryDot color={cat.color} />
                  {cat.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <TextField
          label="Description"
          value={reviewFilterDescription}
          onChange={e => updateReviewFilters.setDescription(e.target.value)}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#fff',
              '&:hover': { backgroundColor: '#fff' },
              '&.Mui-focused': { backgroundColor: '#fff' }
            }
          }}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
            style: { fontSize: '0.875rem' }
          }}
        />
        
        <TextField
          label="Account"
          value={reviewFilterAccount}
          onChange={e => updateReviewFilters.setAccount(e.target.value)}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#fff',
              '&:hover': { backgroundColor: '#fff' },
              '&.Mui-focused': { backgroundColor: '#fff' }
            }
          }}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
            style: { fontSize: '0.875rem' }
          }}
        />
      </ScrollableFilters>
    </Box>
  );

  // Category cell: soft category chip plus the inline AI suggestion
  // (accept/dismiss), shared by the desktop column and the mobile chip row
  const renderCategoryChips = (txn) => {
    const txnId = txn._id || txn.id;
    const isUncategorized = !txn.category || txn.category === 'uncategorized';
    const aiSuggestion = isUncategorized ? aiSuggestionsById.get(txnId) : null;
    const cat = !isUncategorized ? categories.find(c => c.id === txn.category) : null;
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <CategoryChip
          label={cat ? cat.name : 'Uncategorized'}
          color={cat?.color || '#999'}
          sx={{ fontSize: '0.75rem', height: 20 }}
        />
        {aiSuggestion && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            <Chip
              icon={<AutoAwesomeIcon sx={{ fontSize: '0.8rem !important' }} />}
              label={`${aiSuggestion.categoryName}?`}
              size="small"
              variant="outlined"
              color="secondary"
              sx={{ fontSize: '0.75rem', height: 20 }}
            />
            <Tooltip title={`Accept: ${aiSuggestion.categoryName}`}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onAcceptAiSuggestion(aiSuggestion); }}
                sx={{ p: 0.25, color: 'success.main' }}
              >
                <CheckIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Dismiss suggestion">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onRejectAiSuggestion(aiSuggestion.id); }}
                sx={{ p: 0.25, color: 'text.secondary' }}
              >
                <CloseIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    );
  };

  const handleHeaderSort = (column) => {
    const dir = txnSort.type === column && txnSort.direction === 'asc' ? 'desc' : 'asc';
    handleTxnSortChange(column, dir);
  };

  const renderTransactionList = () => {
    if (filtered.length === 0) {
      return (
        <InfoBanner>
          No transactions match your filters. Try adjusting your criteria or adding new transactions.
        </InfoBanner>
      );
    }

    return (
      <>
        <ResponsiveListTable
          items={paginated}
          getId={(t) => t._id || t.id}
          columns={[
            { id: 'date', label: 'Date', render: t => (t.date ? formatRelativeDate(t.date) : ''), sx: { color: 'text.secondary' } },
            { id: 'description', label: 'Description', render: t => t.description || '-', sx: { fontWeight: 600, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' } },
            { id: 'category', label: 'Category', sortable: false, render: renderCategoryChips },
            { id: 'account', label: 'Account', sortable: false, render: t => getAccountName(t.account), sx: { color: 'text.secondary', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' } },
            {
              id: 'amount', label: 'Amount', align: 'right',
              render: t => (
                <Box component="span" sx={{ fontWeight: 700, color: t.amount > 0 ? 'success.main' : 'error.main' }}>
                  {t.amount < 0 ? '-' : '+'}{formatCurrency(t.amount)}
                </Box>
              )
            }
          ]}
          sortBy={txnSort.type}
          sortDirection={txnSort.direction}
          onSort={handleHeaderSort}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onSelectAll={(checked) => setSelectedIds(checked ? filteredIds : [])}
          actions={[
            { key: 'category', label: 'Change Category', icon: <CategoryIcon sx={{ fontSize: 18 }} />, onClick: (t, e) => openCategoryMenu(e, t._id || t.id) },
            { key: 'view', label: 'View', icon: <InfoOutlined sx={{ fontSize: 18 }} />, onClick: (t) => { setViewTxn(t); setViewDialogOpen(true); } },
            { key: 'rule', label: 'Create Rule', icon: <RuleIcon sx={{ fontSize: 18 }} />, onClick: (t) => onCreateRuleFromTransaction(t) }
          ]}
          onRowClick={(t) => { setViewTxn(t); setViewDialogOpen(true); }}
          mobilePrimary={(t) => t.description || '-'}
          mobileSecondary={(t) => `${t.date ? formatRelativeDate(t.date) : ''} • ${getAccountName(t.account)}`}
          mobileChips={renderCategoryChips}
          mobileAmount={(t) => (
            <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', whiteSpace: 'nowrap' }} color={t.amount > 0 ? 'success.main' : 'error.main'}>
              {t.amount < 0 ? '-' : '+'}{formatCurrency(t.amount)}
            </Typography>
          )}
        />
        <ListFooter
          page={reviewPage - 1}
          setPage={(p) => handlePageChange(p + 1)}
          rowsPerPage={reviewRowsPerPage}
          setRowsPerPage={handleRowsPerPageChange}
          totalCount={filtered.length}
          rowsOptions={[10, 25, 50]}
        />
      </>
    );
  };

  return (
    <Paper elevation={2} sx={{ mb: 4, p: 0, borderRadius: 3, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      {/* Card Header — matches the AppCard header anatomy used on Overview */}
      <Box sx={{
        px: 2,
        py: 1.5,
        bgcolor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}>
        <Typography variant="h6" fontWeight="bold" sx={{ m: 0, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary', '& svg': { fontSize: '1.1rem' } }}>
            <FactCheckOutlinedIcon />
          </Box>
          Review Transactions
        </Typography>
      </Box>
      
      <Divider />
      
      {/* Filters */}
      {renderFilters()}
      
      {/* Bulk Selection & Categorize Bar */}
      <Box sx={{ mb: 1.5, px: { xs: 1, sm: 2 }, pt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Row 1: result count (+ selection count) and, on mobile, the sort menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 28 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
            Showing {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
            {selectedIds.length > 0 && ` · ${selectedIds.length} selected`}
          </Typography>
          {/* On mobile the table headers are gone, so sorting lives in a menu */}
          <Box sx={{ display: { xs: 'block', sm: 'none' }, flexShrink: 0 }}>
            <Button
              size="small"
              startIcon={<SortIcon />}
              onClick={handleTxnSortClick}
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Sort by
            </Button>
            <Menu
              anchorEl={txnSortAnchorEl}
              open={Boolean(txnSortAnchorEl)}
              onClose={handleTxnSortClose}
              keepMounted
            >
              <MenuItem sx={{ fontSize: '0.875rem' }} onClick={() => handleTxnSortChange('date', 'asc')}>Date ↑</MenuItem>
              <MenuItem sx={{ fontSize: '0.875rem' }} onClick={() => handleTxnSortChange('date', 'desc')}>Date ↓</MenuItem>
              <MenuItem sx={{ fontSize: '0.875rem' }} onClick={() => handleTxnSortChange('amount', 'asc')}>Amount ↑</MenuItem>
              <MenuItem sx={{ fontSize: '0.875rem' }} onClick={() => handleTxnSortChange('amount', 'desc')}>Amount ↓</MenuItem>
              <MenuItem sx={{ fontSize: '0.875rem' }} onClick={() => handleTxnSortChange('description', 'asc')}>Description ↑</MenuItem>
              <MenuItem sx={{ fontSize: '0.875rem' }} onClick={() => handleTxnSortChange('description', 'desc')}>Description ↓</MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Row 2: bulk actions. On mobile the category picker + Assign fill the
            first line and the secondary actions wrap to their own line. */}
        {selectedIds.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ flex: { xs: 1, sm: '0 1 auto' }, minWidth: { xs: 0, sm: 200 } }}>
              <Select
                value={bulkCategory}
                onChange={(e) => setBulkCategory(e.target.value)}
                displayEmpty
                MenuProps={CATEGORY_MENU_PROPS}
                inputProps={{ 'aria-label': 'Category to assign' }}
                renderValue={(selected) => {
                  const cat = categories.find(c => c.id === selected);
                  if (!cat) {
                    return <Box component="span" sx={{ color: 'text.secondary' }}>Choose category…</Box>;
                  }
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <CategoryDot color={cat.color} />
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cat.name}
                      </Box>
                    </Box>
                  );
                }}
                sx={{
                  fontSize: '0.875rem',
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  // The theme forces 0.9rem on inputs; keep the displayed
                  // value the same size as the Assign button next to it.
                  '& .MuiSelect-select': { fontSize: '0.875rem' }
                }}
              >
                {categories.filter(c => c.id !== 'uncategorized').map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <CategoryDot color={cat.color} />
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cat.name}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              disabled={!bulkCategory}
              onClick={handleBulkCategorize}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, fontSize: '0.875rem', flexShrink: 0, px: 2 }}
            >
              Assign
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexBasis: { xs: '100%', sm: 'auto' } }}>
              <Tooltip title={`Use AI to categorize the ${selectedIds.length} selected transaction(s)`}>
                <span>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    disabled={aiCategorizing}
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: '0.9rem !important' }} />}
                    onClick={() => onAiCategorizeSelected(selectedIds)}
                    sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, fontSize: '0.875rem' }}
                  >
                    {aiCategorizing ? 'Thinking...' : 'AI Categorize'}
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                onClick={() => { setSelectedIds([]); setBulkCategory(''); }}
                sx={{ textTransform: 'none', fontSize: '0.875rem', color: 'text.secondary' }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Transaction List */}
  <Box sx={{ mb: 2, px: { xs: 1, sm: 2 }, pt: 1 }}>
        {renderTransactionList()}
      </Box>

      {/* All Filters Dialog */}
      <Dialog 
        open={allFiltersDialogOpen} 
        onClose={closeAllFiltersDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '80vh',
            p: { xs: 1, sm: 3 }
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1,
          px: { xs: 1, sm: 3 }
        }}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Filters
          </Typography>
          <Button onClick={closeAllFiltersDialog} size="small">
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, px: { xs: 1, sm: 3 } }}>
          <Stack spacing={3}>
            {/* Date Range Section */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Date Range
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={reviewFilterStartDate}
                  onChange={e => updateReviewFilters.setStartDate(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="End Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={reviewFilterEndDate}
                  onChange={e => updateReviewFilters.setEndDate(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Stack>
            </Box>

            {/* Category Section */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Category
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Category</InputLabel>
                <Select
                  value={reviewFilterCategory}
                  label="Category"
                  onChange={e => updateReviewFilters.setCategory(e.target.value)}
                  displayEmpty
                  MenuProps={CATEGORY_MENU_PROPS}
                  renderValue={(selected) => {
                    if (!selected || selected === '') return 'All Categories';
                    if (selected === 'uncategorized') return 'Uncategorized';
                    const cat = categories.find(c => c.id === selected);
                    return cat ? cat.name : 'All Categories';
                  }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="uncategorized">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CategoryDot color="#999" />
                      Uncategorized
                    </Box>
                  </MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryDot color={cat.color} />
                        {cat.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Search Section */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Search
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Description"
                  value={reviewFilterDescription}
                  onChange={e => updateReviewFilters.setDescription(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                <TextField
                  label="Account"
                  value={reviewFilterAccount}
                  onChange={e => updateReviewFilters.setAccount(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 1, sm: 3 }, pb: 3, pt: 2 }}>
          <Button 
            onClick={clearAllFiltersAndCloseDialog}
            sx={{ textTransform: 'none' }}
          >
            Clear all
          </Button>
          <Button
            onClick={closeAllFiltersDialog}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category quick-select menu (capped height — 30+ categories need scroll) */}
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={categoryMenuPos || undefined}
        open={Boolean(categoryMenuPos)}
        onClose={closeCategoryMenu}
        slotProps={{ paper: { sx: { ...CATEGORY_MENU_PROPS.PaperProps.sx, width: 280 } } }}
      >
        {categories
          .filter(c => c.id !== 'uncategorized')
          .map(cat => (
            <MenuItem key={cat.id} onClick={() => handleSelectCategory(cat.id)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryDot color={cat.color} />
                {cat.name}
              </Box>
            </MenuItem>
          ))}
      </Menu>

      {/* View Transaction Dialog (mirrors Transactions List) */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            View Transaction
            <IconButton onClick={() => setViewDialogOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewTxn && (
            <Box sx={{ pt: 1 }}>
              <InlineRow>
                <InlineLabel>ID</InlineLabel>
                <InlineInputBox>
                  <TextField fullWidth value={viewTxn._id || viewTxn.id || ''} InputProps={{ readOnly: true }} />
                </InlineInputBox>
              </InlineRow>

              <InlineRow>
                <InlineLabel>Date</InlineLabel>
                <InlineInputBox>
                  <TextField fullWidth value={formatLocalDate(viewTxn.date)} InputProps={{ readOnly: true }} />
                </InlineInputBox>
              </InlineRow>

              <InlineRow>
                <InlineLabel>Amount</InlineLabel>
                <InlineInputBox>
                  <TextField fullWidth value={`${viewTxn.amount < 0 ? '−' : '+'}${formatCurrency(viewTxn.amount || 0)}`} InputProps={{ readOnly: true }} />
                </InlineInputBox>
              </InlineRow>

              <InlineRow>
                <InlineLabel>Balance</InlineLabel>
                <InlineInputBox>
                  <TextField fullWidth value={formatCurrency(viewTxn.balance || 0)} InputProps={{ readOnly: true }} />
                </InlineInputBox>
              </InlineRow>

              <InlineRow>
                <InlineLabel>Category</InlineLabel>
                <InlineInputBox>
                  <TextField fullWidth value={getCategoryName(viewTxn.category)} InputProps={{ readOnly: true }} />
                </InlineInputBox>
              </InlineRow>

              <InlineRow>
                <InlineLabel>Account</InlineLabel>
                <InlineInputBox>
                  <TextField fullWidth value={viewTxn.account || ''} InputProps={{ readOnly: true }} />
                </InlineInputBox>
              </InlineRow>

              <InlineRow>
                <InlineLabel>Description</InlineLabel>
                <InlineInputBox>
                  <TextField fullWidth value={viewTxn.description || ''} InputProps={{ readOnly: true }} />
                </InlineInputBox>
              </InlineRow>

              <InlineRow>
                <InlineLabel>Type</InlineLabel>
                <InlineInputBox>
                  <TextField fullWidth value={viewTxn.type || ''} InputProps={{ readOnly: true }} />
                </InlineInputBox>
              </InlineRow>

              <InlineRow>
                <InlineLabel>Notes</InlineLabel>
                <InlineInputBox>
                  <TextField fullWidth multiline minRows={2} value={viewTxn.notes || ''} InputProps={{ readOnly: true }} />
                </InlineInputBox>
              </InlineRow>

              {viewTxn.meta && (
                <InlineRow>
                  <InlineLabel>Meta</InlineLabel>
                  <InlineInputBox>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      value={(() => { try { return JSON.stringify(viewTxn.meta, null, 2); } catch { return String(viewTxn.meta); } })()}
                      InputProps={{ readOnly: true }}
                    />
                  </InlineInputBox>
                </InlineRow>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} color="inherit">Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TransactionReviewCard;